import { Link } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import Reveal from '@/components/Reveal';
import Seo from '@/components/Seo';
import GoogleReviews from '@/components/GoogleReviews';
import InstagramEmbed from '@/components/InstagramEmbed';
import { INSTAGRAM_TESTIMONIAL_EMBEDS } from '@/constants/reviews';
import { SOCIAL } from '@/constants/site';

export default function Testimonials() {
  const hasEmbeds = INSTAGRAM_TESTIMONIAL_EMBEDS.length > 0;

  return (
    <div className="bg-[hsl(var(--bb-ivory))]" data-testid="testimonials-page">
      <Seo
        title="Client Testimonials — Bombay Bespoke Fashion House | Dubai"
        description="Real reviews and Instagram stories from clients of Bombay Bespoke Fashion House and VM by Vandana — brides, grooms, and everyday couture clients across Dubai."
        canonical="https://bombaybespokefashion.com/testimonials"
      />

      {/* HERO */}
      <section className="bb-container pt-14 md:pt-24 pb-10">
        <Reveal>
          <div className="bb-eyebrow">Testimonials</div>
        </Reveal>
        <div className="grid grid-cols-12 gap-10 mt-4">
          <div className="col-span-12 lg:col-span-8">
            <Reveal delay={100}>
              <h1 className="bb-display text-5xl md:text-7xl lg:text-8xl text-[hsl(var(--bb-text))]">
                In the words
                <br />
                of those who
                <br />
                <span className="bb-italic text-[hsl(var(--bb-gold))]">wear it.</span>
              </h1>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pt-6">
            <Reveal delay={180}>
              <p className="text-[hsl(var(--bb-text-muted))] text-base md:text-lg leading-relaxed">
                Three decades of craftsmanship; one promise — every garment is made for
                the person who wears it. Read what our brides, grooms and everyday clients
                have to say.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INSTAGRAM EMBEDS */}
      <section
        className="bg-[hsl(var(--bb-ivory-soft))] border-y border-[hsl(var(--bb-text))]/8"
        data-testid="instagram-testimonials"
      >
        <div className="bb-container py-20 md:py-28">
          <div className="grid grid-cols-12 gap-10 items-end mb-10 md:mb-14">
            <div className="col-span-12 lg:col-span-8">
              <Reveal>
                <div className="bb-eyebrow-emerald">From Instagram</div>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="bb-display text-4xl md:text-5xl mt-3 text-[hsl(var(--bb-text))]">
                  Real moments,
                  <span className="bb-italic text-[hsl(var(--bb-emerald))]"> on tape.</span>
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <Reveal delay={180}>
                <a
                  href={SOCIAL.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 px-5 py-3 border border-[hsl(var(--bb-emerald))] text-[hsl(var(--bb-emerald))] uppercase tracking-[0.22em] text-[11px] font-medium hover:bg-[hsl(var(--bb-emerald))] hover:text-[hsl(var(--bb-text))] transition-colors duration-500"
                >
                  <Instagram size={14} />
                  Follow @vmandbb
                </a>
              </Reveal>
            </div>
          </div>

          {hasEmbeds ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {INSTAGRAM_TESTIMONIAL_EMBEDS.map((item, i) => (
                <InstagramEmbed key={i} url={item.url} caption={item.caption} />
              ))}
            </div>
          ) : (
            <Reveal>
              <div
                data-testid="instagram-empty-state"
                className="border border-dashed border-[hsl(var(--bb-text))]/20 p-10 md:p-16 text-center max-w-3xl mx-auto"
              >
                <Instagram size={28} className="mx-auto text-[hsl(var(--bb-gold))]" />
                <h3 className="bb-display text-3xl md:text-4xl mt-5 text-[hsl(var(--bb-text))]">
                  Videos arriving <span className="bb-italic text-[hsl(var(--bb-gold))]">shortly.</span>
                </h3>
                <p className="mt-4 text-[hsl(var(--bb-text-muted))] leading-relaxed">
                  We are curating client reels and reveal videos from{' '}
                  <a
                    href={SOCIAL.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="underline decoration-[hsl(var(--bb-gold))]/60 hover:text-[hsl(var(--bb-gold))]"
                  >
                    @vmandbb
                  </a>{' '}
                  to feature here. In the meantime, visit our Instagram for the
                  latest looks, fittings and finished pieces.
                </p>
                <a
                  href={SOCIAL.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-7 inline-flex items-center gap-3 px-6 py-3.5 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.22em] text-[11px] font-semibold hover:bg-[hsl(var(--bb-text))] transition-colors duration-500"
                >
                  Watch on Instagram
                </a>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* WRITTEN GOOGLE REVIEWS — full list */}
      <GoogleReviews variant="page" />

      {/* CTA */}
      <section className="bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] py-20 md:py-28 bb-grain relative overflow-hidden">
        <div className="relative z-10 bb-container text-center max-w-3xl mx-auto">
          <Reveal>
            <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">Be our next story</div>
          </Reveal>
          <Reveal delay={120}>
            <h3 className="bb-display text-4xl md:text-6xl mt-4">
              Your garment <span className="bb-italic text-[hsl(var(--bb-gold))]">begins here.</span>
            </h3>
          </Reveal>
          <Reveal delay={240}>
            <Link
              to="/book-appointment"
              className="mt-10 inline-flex items-center gap-3 px-7 py-4 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.24em] text-[11px] font-semibold hover:bg-[hsl(var(--bb-text))] transition-colors duration-500"
            >
              Book an appointment
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
